import React, { useEffect, useRef, useState } from 'react';
import L, { LatLngTuple, Map as LeafletMap } from 'leaflet';
import { MapContainer, TileLayer, ZoomControl, useMap, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { ResponseDataGet } from '../../../../models/responseDataGet';
import { EntTerritorialsUnits } from '../../../../models/handBooks/territorialsUnits';
import { FertilizerResponse, Fertilizer, FertilizerMapResponse } from '../api/types';
import styles from "./styles_map.module.css";
import LeasingSelectYear from '../../../mechanization/leasing/Leasingselect';



interface MineralMapProps {
  latitude: number;
  longitude: number;
  polygonsData?: ResponseDataGet<EntTerritorialsUnits>;
  selectedYear: number | null;
  onYearChange: (value: number | null) => void;
  fertilizersData?: FertilizerResponse;
  fertilizersOnMapData?: FertilizerMapResponse;
  isLoadingFertilizers: boolean;
  isLoadingFertilizersOnMap: boolean;
  onRegionClick: (regionId: number, regionName: string) => void;
  onResetRegion: () => void;
  selectedRegionName: string;
  initialRegionId?: number;
}

const MapComponent: React.FC<{
  polygonsData?: ResponseDataGet<EntTerritorialsUnits>;
  onRegionClick: (regionId: number, regionName: string) => void;
  initialRegionId?: number;
}> = ({ polygonsData, onRegionClick, initialRegionId }) => {
  const map = useMap();
  const polygonLayersRef = useRef<{[key: string]: L.Polygon}>({});
  const activePolygonRef = useRef<L.Polygon | null>(null);

  const setActivePolygonStyle = React.useCallback((polygonLayer: L.Polygon) => {
    if (activePolygonRef.current !== polygonLayer) {
      Object.values(polygonLayersRef.current).forEach(layer => {
        layer.setStyle({
          fillOpacity: 0.1,
          weight: 1,
          color: 'rgba(77, 142, 33, 1)',
          fillColor: 'rgba(77, 142, 33, 1)'
        });
      });

      polygonLayer.setStyle({
        fillOpacity: 0.3,
        weight: 2,
        color: 'green',
        fillColor: 'green'
      });
      activePolygonRef.current = polygonLayer;
    }
  }, []);

  useEffect(() => {
    if (!map || !polygonsData?.res) return;

    const polygonsArray = Array.isArray(polygonsData.res) ? polygonsData.res : [polygonsData.res];

    Object.values(polygonLayersRef.current).forEach(layer => layer.remove());
    polygonLayersRef.current = {};

    polygonsArray.forEach((polygon: EntTerritorialsUnits) => {
      try {
        const parsedPolygon = JSON.parse(polygon.polygonJson);
        if (!parsedPolygon || !Array.isArray(parsedPolygon) || !parsedPolygon[0]) return;

        const geoJson = parsedPolygon[0].geojson;
        if (!geoJson || !geoJson.coordinates || !Array.isArray(geoJson.coordinates)) return;

        const coordinates = geoJson.type === 'MultiPolygon' 
          ? geoJson.coordinates[0][0]  
          : geoJson.coordinates[0];    

        if (!Array.isArray(coordinates)) return;

        const latLngs: [number, number][] = coordinates.map((coord: number[]) => [coord[1], coord[0]]);

        const polygonLayer = L.polygon(latLngs, { 
          color: 'rgba(77, 142, 33, 1)',
          fillColor: 'rgba(77, 142, 33, 1)',
          fillOpacity: 0.1,
          weight: 1,
          className: styles.clickablePolygon
        })
        .addTo(map);

        polygonLayer.on('click', () => {
          if (polygon.id) {
            setActivePolygonStyle(polygonLayer);
            onRegionClick(polygon.id, polygon.nameRu!);
          }
        });

        if (polygon.id != null) {
          polygonLayersRef.current[polygon.id.toString()] = polygonLayer;
          
          if (initialRegionId && polygon.id === initialRegionId) {
            setActivePolygonStyle(polygonLayer);
          }
        }
      } catch (error) {
        console.error(`Error processing polygon for ${polygon.nameRu}:`, error);
      }
    });

    if (Object.values(polygonLayersRef.current).length > 0) {
      const group = L.featureGroup(Object.values(polygonLayersRef.current));
      map.fitBounds(group.getBounds());
    }
  }, [map, polygonsData, onRegionClick, setActivePolygonStyle, initialRegionId]);

  return null;
};

const customMarkerIcon = L.icon({
  iconUrl: '/mineral.svg',
  iconSize: [35, 55],
  iconAnchor: [17, 54],
  popupAnchor: [1, -34],
});

export const MineralMap: React.FC<MineralMapProps> = ({
  latitude,
  longitude,
  polygonsData,
  selectedYear,
  onYearChange,
  fertilizersData,
  fertilizersOnMapData,
  isLoadingFertilizers,
  isLoadingFertilizersOnMap,
  onRegionClick,
  onResetRegion,
  selectedRegionName,
  initialRegionId
}) => {
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('company');
  const [selectedType, setSelectedType] = useState<'nitrogen' | 'phosphorus' | 'potassium' | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);

  useEffect(() => {
    if (fertilizersData?.res) {
      setSelectedCompanies(fertilizersData.res.map(supplier => supplier.companyName));
    }
  }, [fertilizersData]);

  const getFilteredMarkers = () => {
    if (activeTab === 'company') {
      if (!fertilizersData?.res) return [];
      if (selectedCompanies.length === 0) return [];
      
      const markers: { companyName: string; address: string; latitude: number; longitude: number; fertilizers: any[] }[] = [];
      
      fertilizersData.res.forEach(company => {
        if (selectedCompanies.includes(company.companyName)) {
          company.addresses.forEach(address => {
            markers.push({
              companyName: company.companyName,
              address: address.addressName,
              latitude: address.latitude,
              longitude: address.longitude,
              fertilizers: address.fertilizers
            });
          });
        }
      });
      
      return markers;
    }
    return [];
  };

  const calculateFertilizerStats = () => {
    if (!fertilizersOnMapData?.res) return null;

    const stats = {
      totalAmount: 0,
      nitrogen: 0,
      phosphorus: 0,
      potassium: 0,
      companies: new Map<string, number>()
    };

    fertilizersOnMapData.res.forEach(item => {
      stats.totalAmount += item.totalAvailableQuantity;
      
      
      const type = item.fertilizerInfo.fertilizerTypeRu.toLowerCase();
      if (type.includes('аммиа') || type.includes('карбамид') || type.includes('азот')) {
        stats.nitrogen += item.totalAvailableQuantity;
      } else if (type.includes('фосфор') || type.includes('суперфосфат')) {
        stats.phosphorus += item.totalAvailableQuantity;
      } else if (type.includes('калий') || type.includes('калимагнезия')) {
        stats.potassium += item.totalAvailableQuantity;
      }

    
      stats.companies.set(item.companyName, item.warehouseCount);
    });

    return stats;
  };

  const renderFertilizerStats = () => {
    if (isLoadingFertilizersOnMap) {
      return <div className={styles.loading}>Загрузка...</div>;
    }

    const stats = calculateFertilizerStats();
    if (!stats) return null;

    return (
      <div className={styles.statsContainer}>
        <div className={styles.statsInfo}>
          <div className={styles.statsSection}>
            <div className={styles.statsSectionTitle}>Информация</div>
            <div className={styles.statsRow}>
              <span>Общий объем удобрений</span>
              <span>{stats.totalAmount} кг</span>
            </div>
            <div className={styles.statsRow}>
              <span>Фосфор</span>
              <span>{stats.phosphorus} кг</span>
            </div>
            <div className={styles.statsRow}>
              <span>Калий</span>
              <span>{stats.potassium} кг</span>
            </div>
            <div className={styles.statsRow}>
              <span>Азот</span>
              <span>{stats.nitrogen} кг</span>
            </div>
          </div>

          <div className={styles.statsSection}>
            <div className={styles.statsSectionTitle}>Компании</div>
            {Array.from(stats.companies.entries()).map(([company, count]) => (
              <div key={company} className={styles.companyRow}>
                <span>{company}</span>
                <span>{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && fertilizersData?.res) {
      setSelectedCompanies(fertilizersData.res.map(supplier => supplier.companyName));
    } else {
      setSelectedCompanies([]);
    }
  };

  const handleCompanySelect = (companyName: string, checked: boolean) => {
    if (checked) {
      setSelectedCompanies(prev => [...prev, companyName]);
    } else {
      setSelectedCompanies(prev => prev.filter(name => name !== companyName));
    }
  };

  const renderFertilizerInfo = (fertilizers: Fertilizer[]) => {
    return (
      <div className={styles.popupContent}>
        {fertilizers.map((fertilizer, index) => (
          <div key={`${fertilizer.fertilizerId}-${index}`} className={styles.fertilizerItem}>
            <p><strong>{fertilizer.fertilizerTypeRu}</strong></p>
            <p>В наличии: {fertilizer.availableQuantity} кг</p>
            <p>Цена за кг: {fertilizer.pricePerKg} сом</p>
            <p>Распределено: {fertilizer.distributedQuantity} кг</p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.mapWrapper}>
      <div className={styles.yearSelector}>
            <LeasingSelectYear value={selectedYear} onChange={onYearChange} />
          </div>
        <MapContainer
          center={[latitude, longitude] as LatLngTuple}
          zoom={6}
          minZoom={6}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
        >
        
    <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
          <ZoomControl position="bottomright" />
          {polygonsData && <MapComponent polygonsData={polygonsData} onRegionClick={onRegionClick} initialRegionId={initialRegionId} />}
          
          {!isLoadingFertilizers && getFilteredMarkers().map((marker) => (
            <Marker
              key={`${marker.companyName}-${marker.latitude}-${marker.longitude}`}
              position={[marker.latitude, marker.longitude]}
              icon={customMarkerIcon}
            >
              <Popup>
                <div className={styles.popupHeader}>
                  <h3>{marker.companyName}</h3>
                  <p>{marker.address}</p>
                </div>
                {renderFertilizerInfo(marker.fertilizers)}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      <div className={styles.tableContainer}>
        <div className={styles.filterHeader}>
          <div className={styles.headerContainer}>
            <h3 className={styles.headerTitle}>
              {selectedRegionName ? `${selectedRegionName}` : 'Данные по республике'}
            </h3>
            {selectedRegionName && (
              <button 
                className={styles.resetButton}
                onClick={onResetRegion}
                title="Показать данные по всей республике"
              >
                ×
              </button>
            )}
          </div>
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${activeTab === 'company' ? styles.active : ''}`}
              onClick={() => setActiveTab('company')}
            >
              По компаниям
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'fertilizer' ? styles.active : ''}`}
              onClick={() => setActiveTab('fertilizer')}
            >
              По удобрениям
            </button>
          </div>
        </div>

        {activeTab === 'company' && fertilizersData?.res && (
          <>
            <div className={styles.listHeader}>
              <span>Название</span>
              <span>Склады</span>
            </div>
            <ul className={styles.companyList}>
              <li className={styles.companyItem}>
                <input
                  type="checkbox"
                  checked={selectedCompanies.length === (fertilizersData.res?.length || 0)}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
                <span className={styles.companyName}>Все</span>
              </li>
              {fertilizersData.res.map((company) => (
                <li key={company.companyName} className={styles.companyItem}>
                  <input
                    type="checkbox"
                    checked={selectedCompanies.includes(company.companyName)}
                    onChange={(e) => handleCompanySelect(company.companyName, e.target.checked)}
                  />
                  <span className={styles.companyName}>{company.companyName}</span>
                  <span className={styles.address}>{company.totalWarehouses} складов</span>
                </li>
              ))}
            </ul>
          </>
        )}

        {activeTab === 'fertilizer' && renderFertilizerStats()}
      </div>
    </div>
  );
};
